/**
 * @fileoverview Shared theme system for FireSquirrel admin grid surfaces — defines the
 * GridTheme token catalog, the scoped CSS generator consumed by GridPageShell-based
 * grids, and the React context/provider that resolves and persists the active theme.
 * @module ui-admin/contexts/ThemeContext
 *
 * Responsibilities:
 * - Define the GridTheme token contract and the 11 built-in theme definitions (colors
 *   for page/sidebar/card/table/text/accent/input surfaces).
 * - Generate scoped `.theme-grid` CSS (themeCSS()) that maps hardcoded Tailwind utility
 *   classes used across grid pages to the active theme's tokens.
 * - Derive chart-specific color tokens (getChartTheme()) for ECharts option builders.
 * - Provide ThemeContext/useTheme/useThemeCSS and the ThemeProvider that resolves the
 *   active theme id, persists it to localStorage, and injects CSS custom properties
 *   (--brand-primary, --tp-panel-*) onto the document root.
 *
 * Dependencies:
 * - React context/state only; no external theming library.
 * - Host apps' ThemeBootstrap (e.g. system-web, tenant-web) supply `initialThemeId`
 *   from Dexie-sourced user/tenant preferences.
 *
 * Architecture:
 * - Client-side only; the theme id is read from localStorage on first paint and
 *   overridden by the Dexie-sourced `initialThemeId` once it resolves.
 * - `themeCSS()` intentionally maps only shared Tailwind utility classes used by grid
 *   pages (badges, banners, buttons, etc.) — it must NOT be extended with logic specific
 *   to a single component. Component-specific on-theme colors (e.g. text sitting on a
 *   solid `accent` background) belong on a dedicated GridTheme field (see `accentContrast`)
 *   consumed directly by that component, not folded into this shared generator.
 *
 * Notes:
 * - `accentContrast` is optional and narrowly scoped: only components needing
 *   text-on-solid-`accent` contrast should read it directly (with a '#ffffff' fallback).
 *   Do not wire it into themeCSS()'s Tailwind-class mappings — a prior rollout that did
 *   so caused unrelated status badge/info banner regressions (reverted in fb35eb923)
 *   despite the token itself being correct.
 */
import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';

// ============================================================
// Theme definitions
// ============================================================

export interface GridTheme {
  id: string;
  name: string;
  // Page / layout
  pageBg: string;
  sidebarBg: string;
  sidebarText: string;
  sidebarTextMuted: string;
  sidebarBorder: string;
  sidebarHoverBg: string;
  sidebarActiveBg: string;
  sidebarActiveText: string;
  headerBg: string;
  headerBorder: string;
  headerText: string;
  headerTextMuted: string;
  // Card / grid
  cardBg: string;
  cardBorder: string;
  toolbarBg: string;
  toolbarBorder: string;
  tableHeaderBg: string;
  tableHeaderText: string;
  rowBg: string;
  rowAltBg: string;
  rowHoverBg: string;
  rowBorder: string;
  expandedBg: string;
  expandedCardBg: string;
  expandedCardBorder: string;
  footerBg: string;
  footerBorder: string;
  // Text
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  // Accent
  accent: string;
  accentLight: string;
  accentText: string;
  /**
   * Foreground color for text/icons rendered directly on top of the solid
   * `accent` background (e.g. a primary button). Optional — callers must
   * fall back to '#ffffff' when absent. Distinct from `accentText`, which
   * is for text on `accentLight` backgrounds, not on `accent` itself.
   * Scoped narrowly: this token is NOT consumed by themeCSS() and must not
   * be wired into shared Tailwind-class mappings there (see the reverted
   * accentContrast rollout — the regression came from touching themeCSS(),
   * not from this token).
   */
  accentContrast?: string;
  // Inputs
  inputBorder: string;
  inputBg: string;
}

export const THEMES: GridTheme[] = [
  {
    id: 'default', name: 'Default Light',
    pageBg: '#f3f4f6',
    sidebarBg: '#111827', sidebarText: '#d1d5db', sidebarTextMuted: '#6b7280', sidebarBorder: '#374151',
    sidebarHoverBg: '#1f2937', sidebarActiveBg: '#ea580c', sidebarActiveText: '#ffffff',
    headerBg: '#ffffff', headerBorder: '#e5e7eb', headerText: '#111827', headerTextMuted: '#6b7280',
    cardBg: '#ffffff', cardBorder: '#e5e7eb',
    toolbarBg: '#f9fafb', toolbarBorder: '#e5e7eb',
    tableHeaderBg: '#f1f2f4', tableHeaderText: '#4b5563',
    rowBg: '#ffffff', rowAltBg: '#f9fafb', rowHoverBg: '#f3f0ff', rowBorder: '#e5e7eb',
    expandedBg: '#f3f4f6', expandedCardBg: '#ffffff', expandedCardBorder: '#e5e7eb',
    footerBg: '#f9fafb', footerBorder: '#e5e7eb',
    textPrimary: '#111827', textSecondary: '#4b5563', textMuted: '#9ca3af',
    accent: '#ea580c', accentLight: '#fff7ed', accentText: '#ea580c', accentContrast: '#ffffff',
    inputBorder: '#d1d5db', inputBg: '#ffffff',
  },
  {
    id: 'high-contrast', name: 'High Contrast',
    pageBg: '#f1f5f9',
    sidebarBg: '#0f172a', sidebarText: '#e2e8f0', sidebarTextMuted: '#64748b', sidebarBorder: '#1e293b',
    sidebarHoverBg: '#1e293b', sidebarActiveBg: '#dc2626', sidebarActiveText: '#ffffff',
    headerBg: '#ffffff', headerBorder: '#94a3b8', headerText: '#0f172a', headerTextMuted: '#475569',
    cardBg: '#ffffff', cardBorder: '#94a3b8',
    toolbarBg: '#f8fafc', toolbarBorder: '#94a3b8',
    tableHeaderBg: '#1e293b', tableHeaderText: '#f1f5f9',
    rowBg: '#ffffff', rowAltBg: '#f8fafc', rowHoverBg: '#e2e8f0', rowBorder: '#cbd5e1',
    expandedBg: '#e2e8f0', expandedCardBg: '#ffffff', expandedCardBorder: '#94a3b8',
    footerBg: '#f8fafc', footerBorder: '#94a3b8',
    textPrimary: '#0f172a', textSecondary: '#334155', textMuted: '#64748b',
    accent: '#dc2626', accentLight: '#fef2f2', accentText: '#dc2626', accentContrast: '#ffffff',
    inputBorder: '#94a3b8', inputBg: '#ffffff',
  },
  {
    id: 'dark-steel', name: 'Dark Steel',
    pageBg: '#1a1a2e',
    sidebarBg: '#0f0f23', sidebarText: '#a5b4fc', sidebarTextMuted: '#4338ca', sidebarBorder: '#1e1b4b',
    sidebarHoverBg: '#1e1b4b', sidebarActiveBg: '#818cf8', sidebarActiveText: '#0f0f23',
    headerBg: '#16213e', headerBorder: '#0f3460', headerText: '#e2e8f0', headerTextMuted: '#94a3b8',
    cardBg: '#16213e', cardBorder: '#0f3460',
    toolbarBg: '#16213e', toolbarBorder: '#0f3460',
    tableHeaderBg: '#0f3460', tableHeaderText: '#a5b4fc',
    rowBg: '#16213e', rowAltBg: '#1a1a2e', rowHoverBg: '#1e2a4a', rowBorder: '#0f3460',
    expandedBg: '#0f172a', expandedCardBg: '#1e2a4a', expandedCardBorder: '#334155',
    footerBg: '#16213e', footerBorder: '#0f3460',
    textPrimary: '#e2e8f0', textSecondary: '#94a3b8', textMuted: '#64748b',
    accent: '#818cf8', accentLight: '#1e1b4b', accentText: '#a5b4fc', accentContrast: '#0f0f23',
    inputBorder: '#334155', inputBg: '#1e2a4a',
  },
  {
    id: 'ocean-blue', name: 'Ocean Blue',
    pageBg: '#eff6ff',
    sidebarBg: '#1e3a5f', sidebarText: '#bfdbfe', sidebarTextMuted: '#60a5fa', sidebarBorder: '#1e40af',
    sidebarHoverBg: '#1e40af', sidebarActiveBg: '#2563eb', sidebarActiveText: '#ffffff',
    headerBg: '#dbeafe', headerBorder: '#93c5fd', headerText: '#1e3a5f', headerTextMuted: '#3b82f6',
    cardBg: '#ffffff', cardBorder: '#93c5fd',
    toolbarBg: '#dbeafe', toolbarBorder: '#93c5fd',
    tableHeaderBg: '#1e40af', tableHeaderText: '#dbeafe',
    rowBg: '#ffffff', rowAltBg: '#eff6ff', rowHoverBg: '#dbeafe', rowBorder: '#bfdbfe',
    expandedBg: '#dbeafe', expandedCardBg: '#ffffff', expandedCardBorder: '#93c5fd',
    footerBg: '#eff6ff', footerBorder: '#93c5fd',
    textPrimary: '#1e3a5f', textSecondary: '#3b82f6', textMuted: '#93c5fd',
    accent: '#2563eb', accentLight: '#dbeafe', accentText: '#1d4ed8', accentContrast: '#ffffff',
    inputBorder: '#93c5fd', inputBg: '#ffffff',
  },
  {
    id: 'forest-green', name: 'Forest Green',
    pageBg: '#f0fdf4',
    sidebarBg: '#14532d', sidebarText: '#bbf7d0', sidebarTextMuted: '#4ade80', sidebarBorder: '#166534',
    sidebarHoverBg: '#166534', sidebarActiveBg: '#16a34a', sidebarActiveText: '#ffffff',
    headerBg: '#dcfce7', headerBorder: '#86efac', headerText: '#14532d', headerTextMuted: '#166534',
    cardBg: '#ffffff', cardBorder: '#86efac',
    toolbarBg: '#dcfce7', toolbarBorder: '#86efac',
    tableHeaderBg: '#166534', tableHeaderText: '#dcfce7',
    rowBg: '#ffffff', rowAltBg: '#f0fdf4', rowHoverBg: '#dcfce7', rowBorder: '#bbf7d0',
    expandedBg: '#dcfce7', expandedCardBg: '#ffffff', expandedCardBorder: '#86efac',
    footerBg: '#f0fdf4', footerBorder: '#86efac',
    textPrimary: '#14532d', textSecondary: '#166534', textMuted: '#86efac',
    accent: '#16a34a', accentLight: '#dcfce7', accentText: '#15803d', accentContrast: '#ffffff',
    inputBorder: '#86efac', inputBg: '#ffffff',
  },
  {
    id: 'warm-sand', name: 'Warm Sand',
    pageBg: '#fefce8',
    sidebarBg: '#451a03', sidebarText: '#e8c95a', sidebarTextMuted: '#d97706', sidebarBorder: '#78350f',
    sidebarHoverBg: '#78350f', sidebarActiveBg: '#d97706', sidebarActiveText: '#451a03',
    headerBg: '#fef3c7', headerBorder: '#fbbf24', headerText: '#451a03', headerTextMuted: '#92400e',
    cardBg: '#fffbeb', cardBorder: '#fbbf24',
    toolbarBg: '#fef3c7', toolbarBorder: '#fbbf24',
    tableHeaderBg: '#92400e', tableHeaderText: '#fef3c7',
    rowBg: '#fffbeb', rowAltBg: '#fefce8', rowHoverBg: '#fef3c7', rowBorder: '#fde68a',
    expandedBg: '#fef3c7', expandedCardBg: '#fffbeb', expandedCardBorder: '#fbbf24',
    footerBg: '#fefce8', footerBorder: '#fbbf24',
    textPrimary: '#451a03', textSecondary: '#78350f', textMuted: '#d97706',
    accent: '#d97706', accentLight: '#fef3c7', accentText: '#b45309', accentContrast: '#0a0a0a',
    inputBorder: '#fbbf24', inputBg: '#fffbeb',
  },
  {
    id: 'purple-haze', name: 'Purple Haze',
    pageBg: '#faf5ff',
    sidebarBg: '#3b0764', sidebarText: '#e9d5ff', sidebarTextMuted: '#a855f7', sidebarBorder: '#581c87',
    sidebarHoverBg: '#581c87', sidebarActiveBg: '#9333ea', sidebarActiveText: '#ffffff',
    headerBg: '#f3e8ff', headerBorder: '#c084fc', headerText: '#3b0764', headerTextMuted: '#6b21a8',
    cardBg: '#ffffff', cardBorder: '#c084fc',
    toolbarBg: '#f3e8ff', toolbarBorder: '#c084fc',
    tableHeaderBg: '#581c87', tableHeaderText: '#e9d5ff',
    rowBg: '#ffffff', rowAltBg: '#faf5ff', rowHoverBg: '#f3e8ff', rowBorder: '#e9d5ff',
    expandedBg: '#f3e8ff', expandedCardBg: '#ffffff', expandedCardBorder: '#c084fc',
    footerBg: '#faf5ff', footerBorder: '#c084fc',
    textPrimary: '#3b0764', textSecondary: '#6b21a8', textMuted: '#a855f7',
    accent: '#9333ea', accentLight: '#f3e8ff', accentText: '#7e22ce', accentContrast: '#ffffff',
    inputBorder: '#c084fc', inputBg: '#ffffff',
  },
  {
    id: 'midnight', name: 'Midnight',
    pageBg: '#0f0f0f',
    sidebarBg: '#0a0a0a', sidebarText: '#d4d4d4', sidebarTextMuted: '#525252', sidebarBorder: '#262626',
    sidebarHoverBg: '#1a1a1a', sidebarActiveBg: '#f97316', sidebarActiveText: '#0a0a0a',
    headerBg: '#171717', headerBorder: '#333333', headerText: '#fafafa', headerTextMuted: '#a3a3a3',
    cardBg: '#1a1a1a', cardBorder: '#333333',
    toolbarBg: '#1a1a1a', toolbarBorder: '#333333',
    tableHeaderBg: '#262626', tableHeaderText: '#a3a3a3',
    rowBg: '#1a1a1a', rowAltBg: '#141414', rowHoverBg: '#262626', rowBorder: '#262626',
    expandedBg: '#141414', expandedCardBg: '#1a1a1a', expandedCardBorder: '#333333',
    footerBg: '#1a1a1a', footerBorder: '#333333',
    textPrimary: '#fafafa', textSecondary: '#a3a3a3', textMuted: '#525252',
    accent: '#f97316', accentLight: '#1c1917', accentText: '#fb923c', accentContrast: '#0a0a0a',
    inputBorder: '#404040', inputBg: '#262626',
  },
  {
    id: 'fire-smoke', name: 'Fire and Smoke',
    pageBg: '#2e2828',
    sidebarBg: '#1e1210', sidebarText: '#ffe4c8', sidebarTextMuted: '#cc8844', sidebarBorder: '#5c3018',
    sidebarHoverBg: '#4a2218', sidebarActiveBg: '#e06010', sidebarActiveText: '#fffaf4',
    headerBg: '#302018', headerBorder: '#5c3018', headerText: '#fff0e0', headerTextMuted: '#d4a070',
    cardBg: '#363030', cardBorder: '#555050',
    toolbarBg: '#322c2c', toolbarBorder: '#555050',
    tableHeaderBg: '#4a2218', tableHeaderText: '#ffe4c8',
    rowBg: '#363030', rowAltBg: '#302a2a', rowHoverBg: '#403838', rowBorder: '#4a4242',
    expandedBg: '#2e2828', expandedCardBg: '#403838', expandedCardBorder: '#555050',
    footerBg: '#322c2c', footerBorder: '#555050',
    textPrimary: '#fff4e8', textSecondary: '#e0c098', textMuted: '#b09070',
    accent: '#f97316', accentLight: '#3d2818', accentText: '#fba04c', accentContrast: '#0a0a0a',
    inputBorder: '#605050', inputBg: '#403838',
  },
  {
    id: 'apwa-fire', name: 'APWA Fire',
    pageBg: '#2c2c2c',
    sidebarBg: '#1a1a1a', sidebarText: '#f4f4f4', sidebarTextMuted: '#888888', sidebarBorder: '#3a3a3a',
    sidebarHoverBg: '#3a3a3a', sidebarActiveBg: '#b22222', sidebarActiveText: '#ffffff',
    headerBg: '#242424', headerBorder: '#b22222', headerText: '#f4f4f4', headerTextMuted: '#999999',
    cardBg: '#333333', cardBorder: '#4a4a4a',
    toolbarBg: '#2e2e2e', toolbarBorder: '#4a4a4a',
    tableHeaderBg: '#383838', tableHeaderText: '#ff8c00',
    rowBg: '#333333', rowAltBg: '#2e2e2e', rowHoverBg: '#3e3e3e', rowBorder: '#424242',
    expandedBg: '#2c2c2c', expandedCardBg: '#3e3e3e', expandedCardBorder: '#4a4a4a',
    footerBg: '#2e2e2e', footerBorder: '#4a4a4a',
    textPrimary: '#f4f4f4', textSecondary: '#bbbbbb', textMuted: '#808080',
    accent: '#b22222', accentLight: '#3a2020', accentText: '#ff8c00', accentContrast: '#ffffff',
    inputBorder: '#555555', inputBg: '#3a3a3a',
  },
  {
    id: 'fire-service', name: 'Fire Service',
    pageBg: '#2c2c2c',
    sidebarBg: '#1c1210', sidebarText: '#f8e0c8', sidebarTextMuted: '#b07040', sidebarBorder: '#4a2818',
    sidebarHoverBg: '#3a1e14', sidebarActiveBg: '#b22222', sidebarActiveText: '#ffffff',
    headerBg: '#282828', headerBorder: '#b22222', headerText: '#f4f4f4', headerTextMuted: '#aaaaaa',
    cardBg: '#333333', cardBorder: '#4a4a4a',
    toolbarBg: '#2e2e2e', toolbarBorder: '#4a4a4a',
    tableHeaderBg: '#3a2218', tableHeaderText: '#ff8c00',
    rowBg: '#333333', rowAltBg: '#2e2e2e', rowHoverBg: '#3e3e3e', rowBorder: '#424242',
    expandedBg: '#2c2c2c', expandedCardBg: '#3e3e3e', expandedCardBorder: '#4a4a4a',
    footerBg: '#2e2e2e', footerBorder: '#4a4a4a',
    textPrimary: '#f4f4f4', textSecondary: '#cccccc', textMuted: '#888888',
    accent: '#b22222', accentLight: '#3a2020', accentText: '#ff8c00', accentContrast: '#ffffff',
    inputBorder: '#555555', inputBg: '#3a3a3a',
  },
];

export const FIRE_SMOKE_CSS = `
@keyframes fireShimmer {
  0%, 100% { background-position: 0% 0%; }
  25% { background-position: 100% 50%; }
  50% { background-position: 50% 100%; }
  75% { background-position: 0% 50%; }
}
@keyframes emberPulse {
  0%, 100% { box-shadow: 0 0 10px rgba(249,115,22,0.12), 0 0 30px rgba(239,68,68,0.05); }
  50% { box-shadow: 0 0 18px rgba(249,115,22,0.25), 0 0 50px rgba(239,68,68,0.1); }
}
@keyframes headerEmber {
  0%, 100% { box-shadow: 0 2px 10px rgba(249,115,22,0.18), 0 0 24px rgba(239,68,68,0.06); }
  33% { box-shadow: 0 2px 14px rgba(249,115,22,0.28), 0 0 32px rgba(239,68,68,0.12); }
  66% { box-shadow: 0 2px 8px rgba(249,115,22,0.15), 0 0 20px rgba(239,68,68,0.04); }
}
@keyframes smokeDrift {
  0%, 100% { background-position: 50% 100%, 20% 0%; }
  33% { background-position: 30% 60%, 70% 40%; }
  66% { background-position: 70% 30%, 40% 80%; }
}
@keyframes borderFlicker {
  0%, 100% { border-color: rgba(74,40,24,0.6); }
  20% { border-color: rgba(100,50,25,0.8); }
  40% { border-color: rgba(74,40,24,0.5); }
  60% { border-color: rgba(90,48,32,0.75); }
  80% { border-color: rgba(74,40,24,0.55); }
}
@keyframes accentFlicker {
  0%, 100% { opacity: 1; }
  10% { opacity: 0.92; }
  20% { opacity: 1; }
  35% { opacity: 0.95; }
  50% { opacity: 1; }
  65% { opacity: 0.88; }
  75% { opacity: 1; }
  90% { opacity: 0.94; }
}
@keyframes textGlow {
  0%, 100% { text-shadow: 0 0 6px rgba(252,217,184,0.15); }
  50% { text-shadow: 0 0 12px rgba(252,217,184,0.3), 0 0 24px rgba(249,115,22,0.1); }
}

/* === Sidebar: slow-crawling fire gradient === */
:is([data-theme="fire-smoke"], [data-theme="fire-service"]) aside {
  background: linear-gradient(
    160deg,
    #18100c 0%, #261810 15%, #4a2218 30%, #6c3018 45%,
    #4a2218 55%, #261810 70%, #3a1e14 85%, #18100c 100%
  ) !important;
  background-size: 300% 300% !important;
  animation: fireShimmer 14s ease infinite !important;
}

/* === Header: pulsing ember glow === */
:is([data-theme="fire-smoke"], [data-theme="fire-service"]) header {
  animation: headerEmber 5s ease-in-out infinite;
  border-image: linear-gradient(90deg, #4a2218, #f97316, #ef4444, #f97316, #4a2218) 1 !important;
}

/* === Page background: drifting smoke === */
[data-theme="fire-smoke"] main {
  background:
    radial-gradient(ellipse at 25% 75%, rgba(108,48,24,0.25) 0%, transparent 55%),
    radial-gradient(ellipse at 75% 25%, rgba(74,34,24,0.2) 0%, transparent 55%),
    #2e2828 !important;
  background-size: 200% 200%, 200% 200%, 100% 100% !important;
  animation: smokeDrift 25s ease infinite !important;
}
[data-theme="fire-service"] main {
  background:
    radial-gradient(ellipse at 25% 75%, rgba(108,48,24,0.18) 0%, transparent 55%),
    radial-gradient(ellipse at 75% 25%, rgba(74,34,24,0.14) 0%, transparent 55%),
    #2c2c2c !important;
  background-size: 200% 200%, 200% 200%, 100% 100% !important;
  animation: smokeDrift 25s ease infinite !important;
}

/* === Cards: warm ember pulse === */
:is([data-theme="fire-smoke"], [data-theme="fire-service"]) .rounded-xl {
  animation: emberPulse 6s ease-in-out infinite;
}

/* === Table header text warm glow === */
:is([data-theme="fire-smoke"], [data-theme="fire-service"]) thead th {
  text-shadow: 0 0 8px rgba(252,217,184,0.25);
}

/* === Active sidebar item fire flicker === */
:is([data-theme="fire-smoke"], [data-theme="fire-service"]) aside a[aria-current="page"],
:is([data-theme="fire-smoke"], [data-theme="fire-service"]) aside [style*="rgb(212, 84, 10)"] {
  animation: accentFlicker 2s ease-in-out infinite;
  box-shadow: 0 0 12px rgba(249,115,22,0.35), 0 0 24px rgba(239,68,68,0.15);
}

/* === Row hover warm inset glow === */
:is([data-theme="fire-smoke"], [data-theme="fire-service"]) tbody tr:hover td {
  box-shadow: inset 0 0 30px rgba(249,115,22,0.06);
  transition: box-shadow 0.3s ease;
}

/* === Card border flicker === */
:is([data-theme="fire-smoke"], [data-theme="fire-service"]) .theme-grid .border-b {
  animation: borderFlicker 3s ease-in-out infinite;
}

/* === Accent buttons warm glow === */
:is([data-theme="fire-smoke"], [data-theme="fire-service"]) button[style*="background: rgb(249, 115, 22)"],
:is([data-theme="fire-smoke"], [data-theme="fire-service"]) .bg-orange-600 {
  box-shadow: 0 0 12px rgba(249,115,22,0.4), 0 0 24px rgba(239,68,68,0.15);
  animation: accentFlicker 2.5s ease-in-out infinite;
}

/* === Floating embers in sidebar === */
@keyframes emberRise1 {
  0% { transform: translateY(0) translateX(0) scale(1); opacity: 0; }
  10% { opacity: 1; }
  50% { transform: translateY(-40vh) translateX(8px) scale(0.8); opacity: 0.7; }
  100% { transform: translateY(-85vh) translateX(-4px) scale(0.3); opacity: 0; }
}
@keyframes emberRise2 {
  0% { transform: translateY(0) translateX(0) scale(1); opacity: 0; }
  15% { opacity: 0.9; }
  50% { transform: translateY(-35vh) translateX(-10px) scale(0.7); opacity: 0.6; }
  100% { transform: translateY(-80vh) translateX(6px) scale(0.2); opacity: 0; }
}
@keyframes emberRise3 {
  0% { transform: translateY(0) translateX(0) scale(1); opacity: 0; }
  12% { opacity: 0.8; }
  50% { transform: translateY(-45vh) translateX(12px) scale(0.6); opacity: 0.5; }
  100% { transform: translateY(-90vh) translateX(-8px) scale(0.2); opacity: 0; }
}

:is([data-theme="fire-smoke"], [data-theme="fire-service"]) aside { overflow: hidden !important; }

:is([data-theme="fire-smoke"], [data-theme="fire-service"]) aside::before,
:is([data-theme="fire-smoke"], [data-theme="fire-service"]) aside::after {
  content: '';
  position: absolute;
  bottom: 20px;
  pointer-events: none;
  border-radius: 50%;
  z-index: 1;
  filter: blur(0.5px);
}

:is([data-theme="fire-smoke"], [data-theme="fire-service"]) aside::before {
  left: 22px;
  width: 3px; height: 3px;
  background: radial-gradient(circle, #ffaa44 0%, #ff6622 60%, transparent 100%);
  box-shadow:
    0 0 4px 1px rgba(255,170,68,0.6),
    18px 5px 3px 1px rgba(255,100,30,0.5),
    36px -8px 4px 1px rgba(255,170,68,0.4),
    8px 12px 3px 1px rgba(255,80,20,0.5),
    44px 18px 3px 1px rgba(255,140,50,0.4),
    26px -15px 2px 0px rgba(255,200,80,0.6),
    50px 8px 3px 1px rgba(255,100,30,0.3);
  animation: emberRise1 4.5s ease-in-out infinite;
}

:is([data-theme="fire-smoke"], [data-theme="fire-service"]) aside::after {
  left: 14px;
  width: 2.5px; height: 2.5px;
  background: radial-gradient(circle, #ffcc66 0%, #ff8833 60%, transparent 100%);
  box-shadow:
    0 0 3px 1px rgba(255,200,100,0.5),
    30px 10px 3px 1px rgba(255,130,40,0.4),
    12px -5px 2px 1px rgba(255,200,80,0.5),
    42px 3px 3px 1px rgba(255,100,30,0.3),
    20px 15px 2px 0px rgba(255,170,60,0.5),
    48px -12px 3px 1px rgba(255,220,100,0.4);
  animation: emberRise2 6s ease-in-out 1.5s infinite;
}

/* Third ember layer via nav::after */
:is([data-theme="fire-smoke"], [data-theme="fire-service"]) aside nav { position: relative; }
:is([data-theme="fire-smoke"], [data-theme="fire-service"]) aside nav::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 30px;
  width: 2px; height: 2px;
  background: radial-gradient(circle, #ffdd88 0%, #ff9944 60%, transparent 100%);
  box-shadow:
    0 0 3px 1px rgba(255,220,130,0.5),
    24px 8px 2px 1px rgba(255,150,50,0.4),
    10px -10px 3px 1px rgba(255,180,70,0.5),
    38px 5px 2px 0px rgba(255,120,40,0.4),
    16px 12px 3px 1px rgba(255,200,90,0.3);
  pointer-events: none;
  border-radius: 50%;
  z-index: 1;
  filter: blur(0.5px);
  animation: emberRise3 5.2s ease-in-out 3s infinite;
}

/* === Scrollbar fire treatment === */
:is([data-theme="fire-smoke"], [data-theme="fire-service"]) ::-webkit-scrollbar { width: 8px; height: 8px; }
[data-theme="fire-smoke"] ::-webkit-scrollbar-track { background: #2e2828; }
[data-theme="fire-service"] ::-webkit-scrollbar-track { background: #2c2c2c; }
:is([data-theme="fire-smoke"], [data-theme="fire-service"]) ::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #5a3020, #3b1510);
  border-radius: 4px;
}
:is([data-theme="fire-smoke"], [data-theme="fire-service"]) ::-webkit-scrollbar-thumb:hover { background: linear-gradient(180deg, #f97316, #5a3020); }

/* === Status badges warm glow === */
:is([data-theme="fire-smoke"], [data-theme="fire-service"]) .rounded-full {
  text-shadow: 0 0 4px currentColor;
}

/* === Input focus fire ring === */
:is([data-theme="fire-smoke"], [data-theme="fire-service"]) input:focus,
:is([data-theme="fire-smoke"], [data-theme="fire-service"]) select:focus {
  box-shadow: 0 0 0 2px rgba(249,115,22,0.4), 0 0 12px rgba(249,115,22,0.2) !important;
  border-color: #f97316 !important;
}

/* === Section headers: fire accent underline + glow === */
:is([data-theme="fire-smoke"], [data-theme="fire-service"]) .theme-grid .border-b.border-gray-200 {
  border-image: linear-gradient(90deg, transparent, rgba(249,115,22,0.5), rgba(239,68,68,0.3), transparent) 1 !important;
}
:is([data-theme="fire-smoke"], [data-theme="fire-service"]) .theme-grid .uppercase.tracking-wider {
  color: #fba04c !important;
  text-shadow: 0 0 8px rgba(249,115,22,0.25);
}

/* === Active tab fire underline === */
:is([data-theme="fire-smoke"], [data-theme="fire-service"]) .theme-grid button[style*="border-bottom: 2px solid"] {
  text-shadow: 0 0 6px rgba(249,115,22,0.3);
}

/* === Page title glow === */
:is([data-theme="fire-smoke"], [data-theme="fire-service"]) .theme-grid h1,
:is([data-theme="fire-smoke"], [data-theme="fire-service"]) .theme-grid h2 {
  text-shadow: 0 0 10px rgba(249,115,22,0.15);
}

/* === Selected row fire highlight === */
:is([data-theme="fire-smoke"], [data-theme="fire-service"]) .theme-grid tr[data-active="true"] td,
:is([data-theme="fire-smoke"], [data-theme="fire-service"]) .theme-grid tbody tr.bg-blue-50\\/40 td {
  box-shadow: inset 0 0 20px rgba(249,115,22,0.1);
}

/* === Service area chip overrides for dark fire theme === */
/* District (blue → warm orange) */
:is([data-theme="fire-smoke"], [data-theme="fire-service"]) .bg-blue-50 { background: rgba(249,115,22,0.15) !important; }
:is([data-theme="fire-smoke"], [data-theme="fire-service"]) .text-blue-700 { color: #fba04c !important; }
:is([data-theme="fire-smoke"], [data-theme="fire-service"]) .border-blue-200 { border-color: rgba(249,115,22,0.35) !important; }
/* Battalion (violet → warm rose-red) */
:is([data-theme="fire-smoke"], [data-theme="fire-service"]) .bg-violet-50 { background: rgba(239,68,68,0.15) !important; }
:is([data-theme="fire-smoke"], [data-theme="fire-service"]) .text-violet-700 { color: #f87171 !important; }
:is([data-theme="fire-smoke"], [data-theme="fire-service"]) .border-violet-200 { border-color: rgba(239,68,68,0.35) !important; }
/* Station Area (emerald → warm gold) */
:is([data-theme="fire-smoke"], [data-theme="fire-service"]) .bg-emerald-50 { background: rgba(255,200,80,0.15) !important; }
:is([data-theme="fire-smoke"], [data-theme="fire-service"]) .text-emerald-700 { color: #fbbf24 !important; }
:is([data-theme="fire-smoke"], [data-theme="fire-service"]) .border-emerald-200 { border-color: rgba(255,200,80,0.35) !important; }
/* Zone (amber → bright amber, already warm but boost contrast) */
:is([data-theme="fire-smoke"], [data-theme="fire-service"]) .bg-amber-50 { background: rgba(255,170,50,0.15) !important; }
:is([data-theme="fire-smoke"], [data-theme="fire-service"]) .text-amber-700 { color: #fcd34d !important; }
:is([data-theme="fire-smoke"], [data-theme="fire-service"]) .border-amber-200 { border-color: rgba(255,170,50,0.35) !important; }
`;

// ============================================================
// themeCSS — generates scoped CSS for .theme-grid wrapper
// ============================================================

export function themeCSS(t: GridTheme): string {
  return `
    .theme-grid { color: ${t.textPrimary}; background: ${t.pageBg}; }
    .theme-grid h1, .theme-grid h2, .theme-grid h3 { color: ${t.textPrimary}; }
    .theme-grid p { color: ${t.textSecondary}; }
    .theme-grid .bg-white.rounded-xl { background: ${t.cardBg}; border-color: ${t.cardBorder}; }
    .theme-grid .border-b.bg-white { background: ${t.toolbarBg}; border-color: ${t.toolbarBorder}; }
    .theme-grid input[type="text"], .theme-grid input[type="search"], .theme-grid input[type="email"], .theme-grid input[type="password"], .theme-grid input[type="number"], .theme-grid textarea, .theme-grid select { background: ${t.inputBg}; border-color: ${t.inputBorder}; color: ${t.textPrimary}; }
    .theme-grid input::placeholder, .theme-grid textarea::placeholder { color: ${t.textMuted}; }
    .theme-grid thead { background: ${t.tableHeaderBg}; }
    .theme-grid thead th { background: ${t.tableHeaderBg}; color: ${t.tableHeaderText} !important; }
    .theme-grid thead th.text-gray-500 { color: ${t.tableHeaderText} !important; }
    .theme-grid thead .bg-gray-50 { background: ${t.tableHeaderBg}; }
    .theme-grid tbody tr { background: ${t.rowBg}; }
    .theme-grid tbody tr:nth-child(even) { background: ${t.rowAltBg}; }
    .theme-grid tbody tr:hover { background: ${t.rowHoverBg}; }
    .theme-grid tbody td { color: ${t.textPrimary}; border-color: ${t.rowBorder}; }
    .theme-grid .divide-y > * + * { border-color: ${t.rowBorder}; }
    .theme-grid .border-t.bg-white { background: ${t.footerBg}; border-color: ${t.footerBorder}; }
    .theme-grid .text-gray-900 { color: ${t.textPrimary}; }
    .theme-grid .text-gray-800 { color: ${t.textPrimary}; }
    .theme-grid .text-gray-700 { color: ${t.textPrimary}; }
    .theme-grid .text-gray-600 { color: ${t.textSecondary}; }
    .theme-grid .bg-white { background: ${t.cardBg}; }
    .theme-grid .bg-gray-50, .theme-grid .bg-gray-50\\/40, .theme-grid .bg-gray-50\\/50 { background: ${t.expandedBg}; }
    .theme-grid .bg-gray-100 { background: ${t.expandedCardBg}; }
    .theme-grid .hover\\:bg-gray-50:hover, .theme-grid .hover\\:bg-gray-100:hover { background: ${t.rowHoverBg}; }
    .theme-grid .border-gray-300 { border-color: ${t.cardBorder}; }
    .theme-grid .text-gray-500 { color: ${t.textSecondary}; }
    .theme-grid .text-gray-400 { color: ${t.textMuted}; }
    .theme-grid .text-gray-300 { color: ${t.textMuted}; }
    .theme-grid .bg-orange-600 { background: ${t.accent}; }
    .theme-grid .hover\\:bg-orange-700:hover { background: ${t.accent}; filter: brightness(0.92); }
    .theme-grid .text-orange-600, .theme-grid .text-orange-700 { color: ${t.accentText}; }
    .theme-grid .bg-orange-50, .theme-grid .bg-orange-50\\/60 { background: ${t.accentLight}; }
    .theme-grid .border-orange-200 { border-color: ${t.accent}40; }
    .theme-grid .border-orange-300 { border-color: ${t.accent}60; }
    .theme-grid td.sticky { background: ${t.rowBg}; }
    .theme-grid th.sticky { background: ${t.tableHeaderBg}; }
    .theme-grid .border-gray-100 { border-color: ${t.expandedCardBorder}; }
    .theme-grid .border-gray-200 { border-color: ${t.cardBorder}; }
    .theme-grid .hover\\:text-gray-700:hover { color: ${t.textPrimary}; }
    .theme-grid .text-orange-500 { color: ${t.accent}; }
    .theme-grid .text-orange-800 { color: ${t.accentText}; }
    .theme-grid .bg-orange-100 { background: ${t.accentLight}; }
    .theme-grid .hover\\:bg-orange-50:hover { background: ${t.accentLight}; }
    .theme-grid .bg-blue-50 { background: ${t.accentLight}; }
    .theme-grid .bg-blue-100 { background: ${t.accentLight}; }
    .theme-grid .border-blue-100 { border-color: ${t.accent}26; }
    .theme-grid .border-blue-200 { border-color: ${t.accent}40; }
    .theme-grid .border-blue-300 { border-color: ${t.accent}60; }
    .theme-grid .text-blue-500 { color: ${t.accent}; }
    .theme-grid .text-blue-600 { color: ${t.accentText}; }
    .theme-grid .text-blue-700 { color: ${t.accentText}; }
    .theme-grid .text-blue-800 { color: ${t.accentText}; }
    .theme-grid .bg-green-100 { background: ${t.accentLight}; }
    .theme-grid .text-green-600 { color: ${t.accent}; }
  `;
}

// ============================================================
// Context
// ============================================================

interface ThemeContextValue {
  theme: GridTheme;
  themeId: string;
  setThemeId: (id: string) => void;
  themes: GridTheme[];
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: THEMES[0],
  themeId: 'default',
  setThemeId: () => {},
  themes: THEMES,
});

export function useTheme() {
  return useContext(ThemeContext);
}

export function useThemeCSS(): string {
  const { theme } = useTheme();
  return useMemo(() => themeCSS(theme), [theme]);
}

/**
 * @contract ChartThemeTokens
 *
 * Purpose:
 * - Derived chart-specific color tokens computed from a GridTheme.
 *
 * Fields:
 * - seriesPrimary: Primary single-series fill color (theme accent).
 * - seriesColors: Multi-series palette anchored to the theme accent.
 * - axisLabelColor: Axis tick label color.
 * - axisLineColor: Axis line and tick color.
 * - splitLineColor: Grid split line color.
 * - tooltipBg: Tooltip panel background.
 * - tooltipBorder: Tooltip panel border.
 * - legendTextColor: Legend label text color.
 *
 * Rules:
 * - All values are derived; no additional fields should be added to GridTheme for charts.
 * - The palette keeps the theme accent as the first color.
 *
 * Sync:
 * - Recomputed synchronously when the active theme changes.
 */
export interface ChartThemeTokens {
  seriesPrimary: string;
  seriesColors: string[];
  axisLabelColor: string;
  axisLineColor: string;
  splitLineColor: string;
  tooltipBg: string;
  tooltipBorder: string;
  legendTextColor: string;
}

/**
 * @process getChartTheme
 *
 * Steps:
 * 1. Extract accent and muted colors from the GridTheme.
 * 2. Derive a 5-color palette from the accent color with semantic secondary colors
 *   (success, warning, danger, info) that are theme-contrast-aware.
 * 3. Map axis/tooltip tokens to the appropriate base theme fields.
 *
 * Inputs:
 * - theme: Active GridTheme.
 *
 * Outputs:
 * - ChartThemeTokens for use in ECharts option builders.
 *
 * Failure Handling:
 * - Falls back to safe defaults if theme fields are empty strings.
 *
 * Idempotency:
 * - REQUIRED
 *
 * Notes:
 * - Secondary colors use semantic fixed hues for consistency across all themes.
 * - These hues work well against both light and dark backgrounds because
 *   ECharts handles chart background rendering separately from the series colors.
 */
export function getChartTheme(theme: GridTheme): ChartThemeTokens {
  // Build a 5-color multi-series palette anchored to the theme accent.
  // Secondary colors use semantic fixed hues that work across all themes.
  // These are standard chart palette colors that provide good contrast
  // and semantic meaning (success, warning, danger, info).
  const seriesColors = [
    theme.accent,
    '#10b981', // emerald — semantic positive
    '#f59e0b', // amber — warning/caution
    '#ef4444', // red — danger/error
    '#8b5cf6', // violet — info/secondary
  ];

  return {
    seriesPrimary: theme.accent,
    seriesColors,
    axisLabelColor: theme.textMuted || '#6b7280',
    axisLineColor: theme.rowBorder || '#e5e7eb',
    splitLineColor: theme.rowBorder || '#e5e7eb',
    tooltipBg: theme.cardBg || '#ffffff',
    tooltipBorder: theme.cardBorder || '#e5e7eb',
    legendTextColor: theme.textSecondary || '#374151',
  };
}

/**
 * @contract ThemeProviderProps
 *
 * Purpose:
 * - Props for the root ThemeProvider.
 *
 * Fields:
 * - children: React subtree to wrap.
 * - initialThemeId: Optional Dexie-sourced effective theme id. When provided it
 *   overrides the localStorage bootstrap on first render and syncs on changes,
 *   making Dexie the authoritative source while localStorage serves as a fast
 *   paint-time cache only.
 *
 * Rules:
 * - If initialThemeId is absent the provider falls back to the localStorage
 *   bootstrap value for backward compatibility during migration.
 *
 * Sync:
 * - App-level ThemeBootstrap children call setThemeId when Dexie resolves the
 *   effective theme so the entire tree updates without a second mount.
 */
interface ThemeProviderProps {
  children: React.ReactNode;
  /** Dexie-sourced effective theme id; overrides localStorage bootstrap when present. */
  initialThemeId?: string;
}

export function ThemeProvider({ children, initialThemeId }: ThemeProviderProps) {
  const [themeId, setThemeIdState] = useState<string>(() => {
    if (initialThemeId) return initialThemeId;
    try { return localStorage.getItem('firesquirrel_theme') || 'default'; } catch { return 'default'; }
  });

  const setThemeId = useCallback((id: string) => {
    setThemeIdState(id);
    try { localStorage.setItem('firesquirrel_theme', id); } catch {}
  }, []);

  // When the Dexie-sourced initialThemeId changes (async load), update the active theme.
  useEffect(() => {
    if (initialThemeId) setThemeIdState(initialThemeId);
  }, [initialThemeId]);

  const theme = THEMES.find(t => t.id === themeId) || THEMES[0];

  useEffect(() => {
    document.documentElement.dataset.theme = themeId;
    document.documentElement.style.setProperty('--brand-primary', theme.accent);
    document.documentElement.style.setProperty('--brand-secondary', '#1C1C1C');
    document.documentElement.style.setProperty('--brand-action', theme.accent);
    document.documentElement.style.setProperty('--tp-panel-bg', theme.cardBg);
    document.documentElement.style.setProperty('--tp-panel-border', theme.cardBorder);
    document.documentElement.style.setProperty('--tp-panel-text', theme.textPrimary);
    document.documentElement.style.setProperty('--tp-panel-text-muted', theme.textMuted);
    document.documentElement.style.setProperty('--tp-panel-surface', theme.expandedBg);
    document.documentElement.style.setProperty('--tp-panel-divide', theme.rowBorder);
    return () => {
      delete document.documentElement.dataset.theme;
      document.documentElement.style.removeProperty('--brand-primary');
      document.documentElement.style.removeProperty('--brand-secondary');
      document.documentElement.style.removeProperty('--brand-action');
      document.documentElement.style.removeProperty('--tp-panel-bg');
      document.documentElement.style.removeProperty('--tp-panel-border');
      document.documentElement.style.removeProperty('--tp-panel-text');
      document.documentElement.style.removeProperty('--tp-panel-text-muted');
      document.documentElement.style.removeProperty('--tp-panel-surface');
      document.documentElement.style.removeProperty('--tp-panel-divide');
    };
  }, [themeId, theme.accent, theme.cardBg, theme.cardBorder, theme.textPrimary, theme.textMuted, theme.expandedBg, theme.rowBorder]);

  return (
    <ThemeContext.Provider value={{ theme, themeId, setThemeId, themes: THEMES }}>
      {(themeId === 'fire-smoke' || themeId === 'fire-service') && <style>{FIRE_SMOKE_CSS}</style>}
      {children}
    </ThemeContext.Provider>
  );
}
